/**
 * Pricing page — types, feature matrix, FAQ, and billing helpers.
 *
 * Plan prices, limits, and discounts live in `pricing.config.json` (real JSON).
 * Edit that file to change what appears on /pricing — this module imports it.
 */

import pricingConfigJson from './pricing.config.json'

export type PlanId = 'small' | 'medium' | 'large' | 'unlimited'
/** The Annual / Monthly toggle on the in-app plans page. */
export type BillingPeriod = 'annual' | 'monthly'
/**
 * How a plan is billed (mirrors Billing::Plans::BILLING_TERMS): monthly on a 1-year contract, monthly with no
 * contract, or annually up front. The in-app page picks one with the toggle plus its "1-year contract" checkbox.
 */
export type BillingTerm = 'monthly_contract' | 'monthly' | 'annual'

/** The only billing term www /pricing shows. No-contract pricing isn't advertised. */
export const ADVERTISED_BILLING_TERM: BillingTerm = 'monthly_contract'

export interface PlanLimits {
    activeSkus: number
    monthlyGmv: number
    maxMarketplaces: number
    users: number
}

/** Raw plan row — edit in pricing.config.json → plans. */
export interface PlanConfig {
    id: PlanId
    name: string
    /**
     * Markets every limit as unlimited: cards and the comparison table say "Unlimited" instead of the number. The
     * limits still apply as soft caps like any paid plan (Billing::Plans::Plan#unlimited?).
     */
    unlimited?: boolean
    /** Monthly price with no contract. */
    monthlyPrice: number
    /** Monthly price on a 1-year contract, the price www /pricing shows. */
    contractMonthlyPrice: number
    /** Total charged once per year on annual billing (already discounted). Source of truth in pricing.config.json. */
    yearlyPrice: number
    limits: PlanLimits
    /** Per-metric overage rates for this plan — charged when usage exceeds the plan's limits. */
    overagePricing: Record<LimitKey, OverageRate>
}

export interface Plan extends PlanConfig {
    featured?: boolean
}

export interface FeatureRow {
    name: string
    description: string
    values: Record<PlanId, boolean | 'unlimited'>
}

export interface FeatureGroup {
    title: string
    features: FeatureRow[]
}

export type LimitKey = keyof PlanLimits

export interface LimitRow {
    name: string
    description: string
    key: LimitKey
    format: 'compactNumber' | 'gmv' | 'number'
}

export interface FaqItem {
    question: string
    /** Plain text, also used as the FAQPage JSON-LD answer. */
    answer: string
    /** Turns the first occurrence of `text` in the answer into a link to the www path `to`. */
    link?: {text: string, to: string}
    /** Only true of www /pricing, which shows contract prices alone; left out of the in-app plans page. */
    wwwOnly?: boolean
}

/** Raw overage rate row — edit in pricing.config.json → overagePricing. */
export interface OverageRate {
    /** Dollar amount per unit of overage — or a fraction (0.05 = 5%) for percent-based metrics. */
    rate: number
    /** Human note describing how the rate applies. */
    calculation: string
}

interface PricingConfigFile {
    featuredPlanId: PlanId
    plans: PlanConfig[]
    freePlanLimits: PlanLimits
}

const {
    featuredPlanId,
    plans: planConfigs,
    freePlanLimits,
} = pricingConfigJson as PricingConfigFile

/** Pricing numbers imported from pricing.config.json */
export const PRICING_CONFIG = {
    featuredPlanId,
    plans: planConfigs,
} as const

/** Free plan limits imported from pricing.config.json */
export const FREE_PLAN_LIMITS: PlanLimits = freePlanLimits

export const PLANS: Plan[] = PRICING_CONFIG.plans.map((plan) => ({
    ...plan,
    featured: plan.id === PRICING_CONFIG.featuredPlanId,
}))

export const LIMIT_ROWS: LimitRow[] = [
    {
        name: 'Active SKUs',
        description: 'SKUs on your active products in Osello, listed or not. This is what counts toward your plan.',
        key: 'activeSkus',
        format: 'compactNumber',
    },
    {
        name: 'Monthly Marketplace GMV',
        description: 'Gross merchandise value from orders on your connected marketplaces in a calendar month.',
        key: 'monthlyGmv',
        format: 'gmv',
    },
    {
        name: 'Max marketplaces',
        description: 'How many retailer channels — Nordstrom, Target+, Macy\'s, and others — you can connect at once.',
        key: 'maxMarketplaces',
        format: 'number',
    },
]

/** Plan cards show a subset of limits — full matrix lives in the comparison table. */
export const PLAN_CARD_LIMIT_ROWS = LIMIT_ROWS.filter((row) => row.key === 'activeSkus' || row.key === 'monthlyGmv')

export const FEATURE_GROUPS: FeatureGroup[] = [
    {
        title: 'Listing',
        features: [
            {
                name: 'Agentic auto list/de-list',
                description: 'AI agents publish new SKUs to marketplaces and remove delisted products without manual listing work.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'AI field transformer',
                description: 'Transforms Shopify product data into each marketplace\'s required taxonomy fields automatically.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Auto split/merge',
                description: 'Splits configurable products into marketplace listings — or merges variants — as each channel requires.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Exclusion filters by tag, style, or SKU',
                description: 'Rules to skip listing certain products by Shopify tag, style name, or SKU.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Custom AI instructions',
                description: 'Brand-level prompts that guide how AI fills listing fields for your voice, rules, and requirements.',
                values: {small: false, medium: true, large: true, unlimited: true},
            },
        ],
    },
    {
        title: 'Optimization',
        features: [
            {
                name: 'AI image studio',
                description: 'Generate and edit marketplace-ready product imagery with AI.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Quick crop, background fix, and touch-ups',
                description: 'Fast image adjustments to meet each retailer\'s image specs.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Marketplace previewer',
                description: 'Preview how listings will appear on each retailer before you publish.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Automatic SEO',
                description: 'AI optimizes titles, descriptions, and attributes for marketplace search.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
        ],
    },
    {
        title: 'Syndication',
        features: [
            {
                name: 'Push orders to Shopify',
                description: 'Marketplace orders sync into Shopify as native orders.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Sync fulfillments from Shopify',
                description: 'Shipment tracking from Shopify flows back to your marketplaces.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Sync refunds to Shopify',
                description: 'Refunds processed in Shopify update the corresponding marketplace orders.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Sync inventory from Shopify',
                description: 'Stock levels from Shopify push to marketplace offers automatically.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Sync product data from Shopify',
                description: 'Product updates in Shopify propagate to listing content on every channel.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Price and offer sync from Shopify',
                description: 'Price and offer changes in Shopify update marketplace listings in one action.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
        ],
    },
    {
        title: 'Other',
        features: [
            {
                name: 'Audit trails',
                description: 'History of who changed what on listings, offers, and submissions.',
                values: {small: false, medium: true, large: true, unlimited: true},
            },
            {
                name: 'All integrations',
                description: 'Connect Shopify and supported external systems for source data.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Two-factor authentication',
                description: 'Secure team logins with two-factor authentication.',
                values: {small: true, medium: true, large: true, unlimited: true},
            },
            {
                name: 'Dedicated account manager',
                description: 'A named Osello contact for onboarding and ongoing support.',
                values: {small: false, medium: false, large: true, unlimited: true},
            },
            {
                name: 'SOC-2 compliance',
                description: 'Infrastructure and processes aligned with SOC-2 standards.',
                values: {small: false, medium: false, large: true, unlimited: true},
            },
            {
                name: 'Security review support',
                description: 'Documentation and assistance for your vendor security reviews.',
                values: {small: false, medium: false, large: true, unlimited: true},
            },
            {
                name: 'Team members',
                description: 'People with login access to your Osello store — invite as many as your team needs.',
                values: {small: 'unlimited', medium: 'unlimited', large: 'unlimited', unlimited: 'unlimited'},
            },
            {
                name: 'SLA',
                description: 'Guaranteed response times and uptime commitments for your team.',
                values: {small: false, medium: false, large: true, unlimited: true},
            },
        ],
    },
]

export const FAQ_ITEMS: FaqItem[] = [
    {
        question: 'What are Active SKUs?',
        answer: 'Active SKUs are the SKUs on your active products in Osello, whether or not they\'re listed on a marketplace. Draft and archived products don\'t count.',
    },
    {
        question: 'What happens if I exceed my limits?',
        answer: 'We never move you to a bigger plan automatically. Instead, overage charges apply for usage '
            + 'beyond your plan limits — a per-day rate for extra Active SKUs, a percentage of GMV above your limit, '
            + 'and a monthly per-unit rate for extra marketplaces. Team members are never charged as overage. '
            + 'Overage rates get lower on higher plans, so upgrading is usually cheaper than sustained overages, '
            + 'and the Unlimited plan never charges overage. '
            + 'The Free plan is the exception: its marketplace and Active SKU limits are hard caps, so you\'ll need to upgrade to go past them.',
    },
    {
        question: 'Can I use Osello without order or offer sync?',
        answer: 'Yes. Order syndication and offer feed sync are optional — you can use Osello\'s AI listing creation and maintenance without them.',
    },
    {
        question: 'Can I start with a smaller plan and upgrade later?',
        answer: 'Yes. Upgrades are prorated automatically. On a 1-year contract, an upgrade starts a new 12-month term.',
    },
    {
        question: 'Can I import existing listings from Mirakl or other providers?',
        answer: 'Yes. Osello supports listing import so you can pick up where you left off. Contact us after signing up and we\'ll help with migration.',
    },
    {
        question: 'Is there a free plan?',
        answer: `Yes. The Free plan includes up to ${formatCompactNumber(FREE_PLAN_LIMITS.activeSkus)} Active SKUs, `
            + `${formatPrice(FREE_PLAN_LIMITS.monthlyGmv)} in monthly marketplace GMV, `
            + `${FREE_PLAN_LIMITS.maxMarketplaces} marketplace${FREE_PLAN_LIMITS.maxMarketplaces === 1 ? '' : 's'}, `
            + `and ${FREE_PLAN_LIMITS.users} team member${FREE_PLAN_LIMITS.users === 1 ? '' : 's'}.`,
    },
    {
        question: 'Why is pricing lower than the competition?',
        answer: 'We\'re AI-native and built without the corporate bloat of legacy tools — so we pass those savings on to you.',
    },
    {
        question: 'Is there a contract?',
        answer: 'Prices on this page assume a 1-year contract, billed monthly. Pricing without a contract, '
            + 'and discounted annual pricing, are also available on request.',
        wwwOnly: true,
    },
    {
        question: 'What if I\'m not using Shopify?',
        answer: 'Simplifying our integration process allows us to keep costs low, so if you\'re not using Shopify you must '
            + 'contact us to confirm that the pricing plans are applicable to you.',
        link: {text: 'contact us', to: '/contact'},
    },
]

// =============================================================================
// Derived pricing helpers — do not edit unless changing billing math
// =============================================================================

export function formatPrice(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(amount)
}

export function formatCompactNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value)
}

export function formatGmv(value: number): string {
    if (value >= 1_000_000) {
        return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`
    }
    if (value >= 1_000) {
        return `$${Math.round(value / 1_000)}K`
    }
    return formatPrice(value)
}

/** The billing term the in-app toggle and its "1-year contract" checkbox select. */
export function billingTermFor(period: BillingPeriod, contract: boolean): BillingTerm {
    if (period === 'annual') return 'annual'
    return contract ? 'monthly_contract' : 'monthly'
}

export function billingLabel(term: BillingTerm): string {
    if (term === 'annual') return 'Billed annually'
    return 'Billed monthly'
    // return term === 'monthly_contract' ? 'Billed monthly' : 'Billed monthly' # Leaving this here in case we want to display contract term in the future.
}

/** Total charged once per year on annual billing — stored in pricing.config.json. */
export function annualTotal(plan: Plan): number {
    return plan.yearlyPrice
}

/** Per-month price on a billing term (annual derived from the stored yearly price). */
export function pricePerMonth(plan: Plan, term: BillingTerm): number {
    if (term === 'monthly') return plan.monthlyPrice
    if (term === 'monthly_contract') return plan.contractMonthlyPrice
    return Math.round(plan.yearlyPrice / 12)
}

/** Monthly billing that annual billing is compared against: on a contract, or without one. */
export type MonthlyTerm = Exclude<BillingTerm, 'annual'>

/** Yearly savings vs 12 monthly payments on `comparedTo`. */
export function annualSavings(plan: Plan, comparedTo: MonthlyTerm): number {
    return pricePerMonth(plan, comparedTo) * 12 - annualTotal(plan)
}

export function formatAnnualSavings(plan: Plan, comparedTo: MonthlyTerm): string {
    return `Save ${formatPrice(annualSavings(plan, comparedTo))}/yr`
}

/** An FAQ answer split around its link, so templates can render the link without `v-html`. */
export function faqAnswerParts(item: FaqItem): {before: string, link: FaqItem['link'] | null, after: string} {
    const index = item.link ? item.answer.indexOf(item.link.text) : -1
    if (!item.link || index < 0) return {before: item.answer, link: null, after: ''}
    return {
        before: item.answer.slice(0, index),
        link: item.link,
        after: item.answer.slice(index + item.link.text.length),
    }
}

/** Each plan's annual discount against `comparedTo`, rounded down so a label never overstates it. */
function annualSavingsPercents(comparedTo: MonthlyTerm): number[] {
    return PLANS.map((plan) => Math.floor((annualSavings(plan, comparedTo) / (pricePerMonth(plan, comparedTo) * 12)) * 100))
}

/** "Save N%" with the smallest saving across the plans, so every plan saves at least that much. */
export function annualSavingsLabel(comparedTo: MonthlyTerm): string {
    return `Save ${Math.min(...annualSavingsPercents(comparedTo))}%`
}

export function lowestPricePerMonth(term: BillingTerm = ADVERTISED_BILLING_TERM): number {
    return Math.min(...PLANS.map((plan) => pricePerMonth(plan, term)))
}

/**
 * Whether a change from a 1-year contract applies now, starting a new term, rather than when the term ends:
 * an equal or bigger plan on a contract or annual billing. Mirrors Billing::Contract.change_effective_now?.
 */
export function contractChangeEffectiveNow(fromPlanId: PlanId, toPlanId: PlanId, toTerm: BillingTerm): boolean {
    const rank = (id: PlanId) => PLANS.findIndex((plan) => plan.id === id)
    return toTerm !== 'monthly' && rank(toPlanId) >= rank(fromPlanId)
}

export function formatLimitValue(plan: Plan, row: LimitRow): string {
    if (plan.unlimited) return 'Unlimited'
    const value = plan.limits[row.key]
    if (row.format === 'compactNumber') return formatCompactNumber(value)
    if (row.format === 'gmv') return formatGmv(value)
    return String(value)
}

